import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Drawer, Input, Popconfirm, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { CalendarCheckIn01Icon, PlusSignSquareIcon } from 'hugeicons-react';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/tr';

import { listInspectionAppointmentsAll } from '../../../modules/inspection-appointments/services/inspection-appointmentService';
import { InspectionAppointment } from '../../../modules/inspection-appointments/types/InspectionAppointment';
import { listLeavesAll } from '../../../modules/leaves/services/leaveService';
import { Leave } from '../../../modules/leaves/types/Leave';
import { listPublicHolidaysAll } from '../../../modules/public-holidays/services/public-holidayService';
import { PublicHoliday } from '../../../modules/public-holidays/types/PublicHoliday';
import {
  createPlanningReservation,
  deletePlanningReservation,
  listPlanningReservationsByRange,
} from '../../../modules/planning-reservations/services/planningReservationService';
import { PlanningReservation } from '../../../modules/planning-reservations/types/PlanningReservation';
import { hasRole, openNotification } from '../../../utils';

dayjs.extend(isoWeek);
dayjs.locale('tr');

const { Text } = Typography;

type AppointmentCellEntry = {
  type: 'appointment';
  appointment: InspectionAppointment;
  planningMoment: Dayjs;
};

type LeaveCellEntry = {
  type: 'leave';
  leave: Leave;
  startMoment: Dayjs;
  endMoment: Dayjs;
  dayStart: Dayjs;
  dayEnd: Dayjs;
  coverStart: Dayjs;
  coverEnd: Dayjs;
};

type PublicHolidayCellEntry = {
  type: 'publicHoliday';
  holiday: PublicHoliday;
  startMoment: Dayjs;
  endMoment: Dayjs;
  dayStart: Dayjs;
  dayEnd: Dayjs;
  coverStart: Dayjs;
  coverEnd: Dayjs;
};

type CellEntry = AppointmentCellEntry | LeaveCellEntry | PublicHolidayCellEntry;

const toNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

const resolveAccountId = (input: any): number | undefined => {
  if (input === null || input === undefined) {
    return undefined;
  }
  if (typeof input === 'number') {
    return Number.isFinite(input) && input > 0 ? input : undefined;
  }
  const candidate =
    (input as any)?.accountId ??
    (input as any)?.account?.id ??
    (input as any)?.account?.accountId ??
    (input as any)?.account?.account?.id ??
    (input as any)?.account?.account?.accountId ??
    (input as any)?.inspectorId ??
    (input as any)?.inspector?.id ??
    (input as any)?.id;
  const parsed = toNumber(candidate);
  return parsed && parsed > 0 ? parsed : undefined;
};

const getEntrySortValue = (entry: CellEntry): number => {
  if (entry.type === 'leave' || entry.type === 'publicHoliday') {
    return entry.coverStart.valueOf();
  }
  return entry.planningMoment.valueOf();
};

interface WeeklyPlanDrawerProps {
  open: boolean;
  onClose: () => void;
  weekStart: Dayjs;
  onWeekStartChange: (week: Dayjs) => void;
  inspectors: any[];
  maxFutureDays?: number;
}

const statusColorMap: Record<string, string> = {
  PLANNED: 'green',
  APPROVED: 'green',
  COMPLETED: 'blue',
  CANCELLED: 'red',
  LEAVE: 'volcano',
  ON_LEAVE: 'volcano',
  PUBLIC_HOLIDAY: 'magenta',
};

const DEFAULT_INSPECTOR_MAX_FUTURE_DAYS = 5;

const leaveTypeMap: Record<string, string> = {
  ANNUAL_PAID: 'Yıllık Ücretli İzin',
  SICK_WITH_REPORT: 'Raporlu Hastalık İzni',
  UNPAID: 'Ücretsiz İzin',
  EXCUSE: 'Mazeret İzni',
  MARRIAGE: 'Evlilik İzni',
  MATERNITY: 'Doğum İzni',
  NURSING: 'Süt İzni',
  PATERNITY: 'Babalık İzni',
  ADOPTION: 'Evlat Edinme İzni',
  BEREAVEMENT: 'Ölüm (Vefat) İzni',
  JOB_SEARCH: 'Yeni İş Arama İzni',
};

const normalizeString = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed.toLocaleUpperCase('tr-TR') : undefined;
  }
  if (typeof value === 'object' && value !== null) {
    const name = (value as any).name;
    if (typeof name === 'string') {
      const trimmed = name.trim();
      return trimmed ? trimmed.toLocaleUpperCase('tr-TR') : undefined;
    }
  }
  return undefined;
};

type AppointmentSourceCollection = {
  objects: any[];
  strings: string[];
};

const collectAppointmentSources = (appointment: InspectionAppointment): AppointmentSourceCollection => {
  const objects: any[] = [];
  const strings: string[] = [];
  const visited = new Set<any>();

  const pushSource = (value: any) => {
    if (!value) return;
    if (typeof value === 'object') {
      if (visited.has(value)) {
        return;
      }
      visited.add(value);
      objects.push(value);
      const nestedAddress = (value as any).address;
      if (nestedAddress && nestedAddress !== value) {
        pushSource(nestedAddress);
      }
    } else if (typeof value === 'string') {
      const normalized = normalizeString(value);
      if (normalized) {
        strings.push(normalized);
      }
    }
  };

  pushSource(appointment);
  pushSource((appointment as any)?.proposal);
  pushSource((appointment as any)?.proposal?.company);
  pushSource((appointment as any)?.proposal?.address);
  pushSource((appointment as any)?.company);
  pushSource((appointment as any)?.company?.address);
  pushSource((appointment as any)?.address);

  return { objects, strings };
};

const pickFromObjects = (sources: any[], keys: string[]): string | undefined => {
  for (const source of sources) {
    if (!source) continue;
    for (const key of keys) {
      const normalized = normalizeString((source as any)[key]);
      if (normalized) {
        return normalized;
      }
    }
  }
  return undefined;
};

type CompanyDetails = {
  name?: string;
  address?: string;
  city?: string;
  district?: string;
};

const getCompanyDetails = (appointment: InspectionAppointment): CompanyDetails => {
  const { objects, strings } = collectAppointmentSources(appointment);

  const pickAddress = (): string | undefined => {
    const fromObjects = pickFromObjects(objects, [
      'fullAddress',
      'address',
      'addressText',
      'addressLine',
      'street',
      'streetAddress',
      'address1',
      'address2',
    ]);
    if (fromObjects) {
      return fromObjects;
    }
    for (const value of strings) {
      if (value) {
        return value;
      }
    }
    return undefined;
  };

  return {
    name:
      pickFromObjects(objects, ['companyName', 'name', 'title', 'firmName']) ??
      normalizeString((appointment as any)?.companyName),
    address: pickAddress(),
    city: pickFromObjects(objects, ['city', 'cityName', 'province', 'provinceName', 'state', 'il']),
    district: pickFromObjects(objects, [
      'district',
      'districtName',
      'district_text',
      'districtText',
      'county',
      'countyName',
      'ilce',
      'town',
    ]),
  };
};

const getPlanningMoment = (appointment: InspectionAppointment): Dayjs | null => {
  const raw = (appointment as any)?.startDate ?? (appointment as any)?.planningAt ?? (appointment as any)?.planning_at;
  if (!raw) return null;
  const moment = dayjs(raw);
  return moment.isValid() ? moment : null;
};

const resolveProfessionName = (value: unknown): string | undefined => {
  if (!value) {
    return undefined;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (typeof value === 'object') {
    const name =
      (value as any)?.name ??
      (value as any)?.title ??
      (value as any)?.professionName;
    if (typeof name === 'string') {
      const trimmed = name.trim();
      return trimmed || undefined;
    }
  }
  return undefined;
};

const getInspectorProfessionNames = (inspector: any): string[] => {
  if (!inspector) {
    return [];
  }
  const names = new Set<string>();
  const pushEntry = (entry: any) => {
    const normalized = resolveProfessionName(entry);
    if (normalized && !names.has(normalized)) {
      names.add(normalized);
    }
  };
  const inspectSource = (source: any) => {
    if (!source) {
      return;
    }
    if (Array.isArray(source)) {
      source.forEach(pushEntry);
      return;
    }
    pushEntry(source);
  };
  inspectSource((inspector as any)?.professions);
  inspectSource((inspector as any)?.account?.professions);
  return Array.from(names);
};

const getInspectorLabel = (inspector: any, fallbackId?: number): string => {
  let baseLabel: string | undefined;
  if (inspector) {
    const account = inspector.account || {};
    const first = account.firstName ? String(account.firstName) : '';
    const last = account.lastName ? String(account.lastName) : '';
    const full = `${first} ${last}`.trim();
    if (full.length) {
      baseLabel = full;
    } else if (typeof inspector.name === 'string' && inspector.name.trim().length) {
      baseLabel = inspector.name.trim();
    }
  }
  if (!baseLabel && typeof fallbackId === 'number' && !Number.isNaN(fallbackId)) {
    baseLabel = `#${fallbackId}`;
  }
  if (!baseLabel) {
    baseLabel = 'Bilinmiyor';
  }
  return baseLabel;
};

const WeeklyPlanDrawer: React.FC<WeeklyPlanDrawerProps> = ({
  open,
  onClose,
  weekStart,
  onWeekStartChange,
  inspectors,
  maxFutureDays,
}) => {
  const isAdmin = useMemo(() => hasRole('ADMIN'), []);
  const isInspector = useMemo(() => hasRole('INSPECTOR'), []);
  const canViewPublicHolidays = useMemo(
    () => isAdmin || hasRole('CENTRAL_USER'),
    [isAdmin],
  );
  const currentUserEmail = useMemo(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const email = localStorage.getItem('email');
    if (!email) {
      return undefined;
    }
    const normalized = email.trim();
    return normalized ? normalized.toLowerCase() : undefined;
  }, []);
  const inspectorFutureLimit = useMemo(() => {
    if (!isInspector) {
      return undefined;
    }
    if (typeof maxFutureDays === 'number' && Number.isFinite(maxFutureDays) && maxFutureDays >= 0) {
      return maxFutureDays;
    }
    return DEFAULT_INSPECTOR_MAX_FUTURE_DAYS;
  }, [isInspector, maxFutureDays]);
  const inspectorSelf = useMemo(() => {
    if (!isInspector) {
      return null;
    }
    if (typeof window === 'undefined') {
      return null;
    }
    const accountIdRaw = localStorage.getItem('id');
    const inspectorIdRaw = localStorage.getItem('inspectorId');
    const firstName = localStorage.getItem('firstName') || '';
    const lastName = localStorage.getItem('lastName') || '';
    const accountId = toNumber(accountIdRaw);
    if (accountId === undefined) {
      return null;
    }
    const inspectorId = toNumber(inspectorIdRaw);
    const primaryId = accountId;
    return {
      id: primaryId,
      inspectorId: inspectorId ?? primaryId,
      account: {
        id: accountId,
        firstName,
        lastName,
      },
    } as any;
  }, [isInspector]);
  const inspectorSelfId = useMemo(() => (inspectorSelf ? toNumber((inspectorSelf as any)?.id) : undefined), [inspectorSelf]);
  const inspectorSelfAccountId = useMemo(
    () => (inspectorSelf ? toNumber((inspectorSelf as any)?.account?.id) : undefined),
    [inspectorSelf],
  );
  const effectiveInspectors = useMemo(() => {
    if (Array.isArray(inspectors) && inspectors.length) {
      return inspectors;
    }
    return inspectorSelf ? [inspectorSelf] : [];
  }, [inspectors, inspectorSelf]);
  const [appointments, setAppointments] = useState<InspectionAppointment[]>([]);
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [appointmentLoading, setAppointmentLoading] = useState<boolean>(false);
  const [leaveLoading, setLeaveLoading] = useState<boolean>(false);
  const [publicHolidays, setPublicHolidays] = useState<PublicHoliday[]>([]);
  const [publicHolidayLoading, setPublicHolidayLoading] = useState<boolean>(false);
  const [reservations, setReservations] = useState<PlanningReservation[]>([]);
  const [reservationLoading, setReservationLoading] = useState<boolean>(false);
  const [reservationBusyKeys, setReservationBusyKeys] = useState<Set<string>>(new Set());
  const [reservationNotes, setReservationNotes] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState<string>('');

  const setReservationBusy = useCallback((key: string, busy: boolean) => {
    setReservationBusyKeys((prev) => {
      const next = new Set(prev);
      if (busy) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  }, []);

  const updateReservationNote = useCallback((key: string | undefined, value: string) => {
    if (!key) {
      return;
    }
    setReservationNotes((prev) => {
      const previousValue = prev[key];
      if (!value && previousValue === undefined) {
        return prev;
      }
      if (previousValue === value) {
        return prev;
      }
      if (!value) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: value };
    });
  }, []);

  const clearReservationNote = useCallback((key: string | undefined) => {
    if (!key) {
      return;
    }
    setReservationNotes((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day')), [weekStart]);
  const publicHolidayRowInfo = useMemo(
    () => ({
      account: {
        firstName: 'Resmi',
        lastName: 'Tatil',
      },
      name: 'Resmi Tatil',
    }),
    [],
  );
  const maxForwardTimestamp = inspectorFutureLimit === undefined
    ? null
    : dayjs().add(inspectorFutureLimit, 'day').endOf('day').valueOf();
  const canNavigateForward = useMemo(() => {
    if (maxForwardTimestamp === null) {
      return true;
    }
    if (!weekDays.length) {
      return true;
    }
    const nextWeekStart = weekDays[0].add(7, 'day').startOf('day');
    return nextWeekStart.valueOf() <= maxForwardTimestamp;
  }, [weekDays, maxForwardTimestamp]);

  useEffect(() => {
    if (!open) {
      return;
    }
    let ignore = false;

    const rangeStart = weekStart.startOf('day');
    const rangeEnd = weekStart.add(6, 'day').endOf('day');
    const params = new URLSearchParams({
      'startDate.greaterThanOrEqual': rangeStart.format('YYYY-MM-DDTHH:mm:ss'),
      'startDate.lessThanOrEqual': rangeEnd.format('YYYY-MM-DDTHH:mm:ss'),
    });
    if (isInspector && inspectorSelfId !== undefined) {
      params.append('accountId.equals', String(inspectorSelfId));
    }
    const query = params.toString();

    const fetchAppointments = async () => {
      try {
        setAppointmentLoading(true);
        const data = await listInspectionAppointmentsAll(query);
        if (!ignore) {
          const items = Array.isArray(data) ? data : [];
          const normalized = items.map((appointment: any) => {
            const accountId = resolveAccountId(appointment);
            return {
              ...appointment,
              accountId,
              inspectorId: accountId ?? (appointment as any)?.inspectorId,
            };
          });
          const filtered = isInspector
            ? normalized.filter((appointment: any) => {
              const inspectorMatchId = inspectorSelfId;
              const accountMatchId = inspectorSelfAccountId;
              const inspectorId = resolveAccountId(appointment);
              if (inspectorMatchId !== undefined && inspectorId === inspectorMatchId) {
                return true;
              }
              if (accountMatchId !== undefined && inspectorId === accountMatchId) {
                return true;
              }
              return false;
            })
            : normalized;
          setAppointments(filtered);
        }

      } catch (error) {
        console.error('Haftalık plan verileri alınamadı:', error);
        if (!ignore) {
          setAppointments([]);
          openNotification('error', 'Hata', 'Haftalık plan verileri yüklenemedi.');
        }
      } finally {
        if (!ignore) {
          setAppointmentLoading(false);
        }
      }
    };

    fetchAppointments();
    return () => {
      ignore = true;
    };
  }, [open, weekStart, isInspector, inspectorSelfId, inspectorSelfAccountId]);

  useEffect(() => {
    if (!open) {
      return;
    }
    let ignore = false;

    const rangeStart = weekStart.startOf('day');
    const rangeEnd = weekStart.add(6, 'day').endOf('day');
    const params = new URLSearchParams({
      sort: 'startDate,ASC',
      'startDate.greaterThanOrEqual': rangeStart.toDate().toISOString(),
      'startDate.lessThanOrEqual': rangeEnd.toDate().toISOString(),
    });
    if (isInspector && inspectorSelfAccountId !== undefined) {
      params.append('accountId.equals', String(inspectorSelfAccountId));
    }
    const query = params.toString();

    const fetchLeaves = async () => {
      try {
        setLeaveLoading(true);
        const data = await listLeavesAll(query);
        if (!ignore) {
          const items = Array.isArray(data) ? data : [];
          const filtered = isInspector && inspectorSelfAccountId !== undefined
            ? items.filter((leave) => {
              const accountId = toNumber((leave as any)?.account?.id ?? (leave as any)?.accountId);
              return accountId !== undefined && accountId === inspectorSelfAccountId;
            })
            : items;
          setLeaves(filtered);
        }
      } catch (error) {
        console.error('Haftalık izin verileri alınamadı:', error);
        if (!ignore) {
          setLeaves([]);
          openNotification('error', 'Hata', 'Haftalık izin verileri yüklenemedi.');
        }
      } finally {
        if (!ignore) {
          setLeaveLoading(false);
        }
      }
    };

    fetchLeaves();

    return () => {
      ignore = true;
    };
  }, [open, weekStart, isInspector, inspectorSelfAccountId]);

  useEffect(() => {
    setReservationBusyKeys(new Set());
    setReservationNotes({});
  }, [weekStart]);

  useEffect(() => {
    if (!open) {
      setReservationNotes({});
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    let ignore = false;

    const fetchPublicHolidays = async () => {
      try {
        setPublicHolidayLoading(true);
        const data = await listPublicHolidaysAll();
        if (ignore) {
          return;
        }
        const items = Array.isArray(data) ? data : [];
        setPublicHolidays(items);
      } catch (error) {
        console.error('Resmi tatil verileri alınamadı:', error);
        if (!ignore) {
          setPublicHolidays([]);
          openNotification('error', 'Hata', 'Resmi tatil verileri yüklenemedi.');
        }
      } finally {
        if (!ignore) {
          setPublicHolidayLoading(false);
        }
      }
    };

    fetchPublicHolidays();

    return () => {
      ignore = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    let ignore = false;

    const rangeStart = weekStart.startOf('day');
    const rangeEnd = weekStart.add(6, 'day').endOf('day');
    const fetchReservations = async () => {
      try {
        setReservationLoading(true);
        const startDateValue = rangeStart.format('YYYY-MM-DD');
        const endDateValue = rangeEnd.format('YYYY-MM-DD');
        const targetAccountId = isInspector ? inspectorSelfAccountId : undefined;
        const data = await listPlanningReservationsByRange(startDateValue, endDateValue, targetAccountId);
        if (!ignore) {
          const items = Array.isArray(data) ? data : [];
          const sorted = items
            .slice()
            .sort((a, b) => {
              const aMoment = dayjs((a as any)?.reservationDate);
              const bMoment = dayjs((b as any)?.reservationDate);
              const aValue = aMoment.isValid() ? aMoment.valueOf() : 0;
              const bValue = bMoment.isValid() ? bMoment.valueOf() : 0;
              return aValue - bValue;
            });
          setReservations(sorted);
        }
      } catch (error) {
        console.error('Rezervasyon verileri alınamadı:', error);
        if (!ignore) {
          setReservations([]);
          openNotification('error', 'Hata', 'Rezervasyon verileri yüklenemedi.');
        }
      } finally {
        if (!ignore) {
          setReservationLoading(false);
        }
      }
    };

    fetchReservations();

    return () => {
      ignore = true;
    };
  }, [open, weekStart, isInspector, inspectorSelfAccountId]);

  const weekAppointments = useMemo(() => {
    if (!weekDays.length) return [];
    const startBoundary = weekDays[0].startOf('day');
    const endBoundary = startBoundary.add(7, 'day');

    return (appointments || []).filter((appointment) => {
      const planningMoment = getPlanningMoment(appointment);
      if (!planningMoment) return false;
      return !planningMoment.isBefore(startBoundary) && planningMoment.isBefore(endBoundary);
    });
  }, [appointments, weekDays]);

  const inspectorById = useMemo(() => {
    const map = new Map<number, any>();
    effectiveInspectors.forEach((ins: any) => {
      const id = resolveAccountId(ins);
      if (id !== undefined) {
        map.set(id, ins);
      }
    });
    return map;
  }, [effectiveInspectors]);

  const inspectorByAccountId = useMemo(() => {
    const map = new Map<number, number>();
    effectiveInspectors.forEach((ins: any) => {
      const accountId = resolveAccountId(ins);
      if (accountId !== undefined) {
        map.set(accountId, accountId);
      }
    });
    return map;
  }, [effectiveInspectors]);

  const accountToInspectorId = useMemo(() => {
    const map = new Map<number, number>();
    const ensureMapping = (key: number | undefined, accountId?: number) => {
      if (key === undefined) {
        return;
      }
      const target = accountId ?? key;
      if (!map.has(key)) {
        map.set(key, target);
      } else if (accountId !== undefined) {
        map.set(key, target);
      }
    };
    inspectorByAccountId.forEach((value, key) => {
      ensureMapping(key, value);
    });
    effectiveInspectors.forEach((ins: any) => {
      const accountId = resolveAccountId(ins);
      const inspectorId = toNumber((ins as any)?.id ?? (ins as any)?.inspectorId);
      ensureMapping(accountId, accountId);
      ensureMapping(inspectorId, accountId ?? inspectorId);
    });
    weekAppointments.forEach((appointment) => {
      const rawInspector = (appointment as any)?.inspector;
      const accountId =
        resolveAccountId(rawInspector) ??
        resolveAccountId((appointment as any)?.account) ??
        resolveAccountId(appointment);
      const inspectorId = toNumber((appointment as any)?.inspectorId ?? (appointment as any)?.inspector?.id);
      ensureMapping(accountId, accountId);
      ensureMapping(inspectorId, accountId ?? inspectorId);
    });
    if (inspectorSelf && inspectorSelfId !== undefined) {
      const selfAccountId = toNumber((inspectorSelf as any)?.account?.id);
      ensureMapping(selfAccountId, selfAccountId);
      ensureMapping(inspectorSelfId, selfAccountId ?? inspectorSelfId);
    }
    (reservations || []).forEach((reservation) => {
      const accountId = resolveAccountId(reservation);
      ensureMapping(accountId, accountId);
    });
    return map;
  }, [inspectorByAccountId, weekAppointments, inspectorSelf, inspectorSelfId, reservations, effectiveInspectors]);

  const inspectorInfoMap = useMemo(() => {
    const map = new Map<number, any>();
    inspectorById.forEach((value, key) => {
      map.set(key, value);
    });
    weekAppointments.forEach((appointment) => {
      const rawInspector = (appointment as any)?.inspector;
      const inspectorId = resolveAccountId(appointment);
      if (inspectorId !== undefined && rawInspector && typeof rawInspector === 'object') {
        if (!map.has(inspectorId)) {
          map.set(inspectorId, rawInspector);
        }
      } else if (rawInspector && typeof rawInspector === 'object') {
        const accountId = resolveAccountId(rawInspector);
        if (accountId !== undefined && !map.has(accountId)) {
          map.set(accountId, rawInspector);
        }
      }
    });
    if (inspectorSelf && inspectorSelfId !== undefined && !map.has(inspectorSelfId)) {
      map.set(inspectorSelfId, inspectorSelf);
    }
    (reservations || []).forEach((reservation) => {
      const accountInfo = (reservation as any)?.account;
      const accountId = resolveAccountId(reservation);
      if (accountId === undefined) {
        return;
      }
      const inspectorId = accountToInspectorId.get(accountId) ?? accountId;
      if (!map.has(inspectorId)) {
        if (accountInfo) {
          map.set(inspectorId, { account: accountInfo });
        } else {
          map.set(inspectorId, reservation);
        }
      }
    });
    return map;
  }, [inspectorById, weekAppointments, inspectorSelf, inspectorSelfId, reservations, accountToInspectorId]);

  const reservationLookup = useMemo(() => {
    const map = new Map<string, PlanningReservation>();
    (reservations || []).forEach((reservation) => {
      const accountId = resolveAccountId(reservation);
      const dateValue = (reservation as any)?.reservationDate;
      if (accountId === undefined || !dateValue) {
        return;
      }
      const dateMoment = dayjs(dateValue);
      if (!dateMoment.isValid()) {
        return;
      }
      const dayKey = dateMoment.format('YYYY-MM-DD');
      map.set(`${accountId}-${dayKey}`, reservation);
    });
    return map;
  }, [reservations]);

  const handleCreateReservation = useCallback(
    async (accountId: number, dayKey: string, note?: string) => {
      if (!dayKey) {
        return;
      }
      const normalizedDayKey = dayjs(dayKey).format('YYYY-MM-DD');
      const busyKey = `create-${accountId}-${normalizedDayKey}`;
      const draftKey = `${accountId}-${normalizedDayKey}`;
      const trimmedNote = note?.trim();
      if (!trimmedNote) {
        openNotification('warning', 'Uyarı', 'Rezervasyon notu zorunludur.');
        return;
      }
      setReservationBusy(busyKey, true);
      try {
        const payload = {
          reservationDate: normalizedDayKey,
          account: { id: accountId },
          note: trimmedNote,
        };
        const saved = await createPlanningReservation(payload);
        setReservations((prev) => {
          const filtered = prev.filter((item) => {
            const itemAccountId = resolveAccountId(item);
            const itemDate = (item as any)?.reservationDate;
            if (itemAccountId === undefined || !itemDate) {
              return true;
            }
            const itemDayKey = dayjs(itemDate).format('YYYY-MM-DD');
            return !(itemAccountId === accountId && itemDayKey === normalizedDayKey);
          });
          if (saved) {
            const savedWithNote =
              trimmedNote && !saved.note ? { ...saved, note: trimmedNote } : saved;
            filtered.push(savedWithNote);
          }
          filtered.sort((a, b) => {
            const aMoment = dayjs((a as any)?.reservationDate);
            const bMoment = dayjs((b as any)?.reservationDate);
            const aValue = aMoment.isValid() ? aMoment.valueOf() : 0;
            const bValue = bMoment.isValid() ? bMoment.valueOf() : 0;
            return aValue - bValue;
          });
          return filtered;
        });
        clearReservationNote(draftKey);
        openNotification('success', 'Başarılı', 'Gün rezerve edildi.');
      } catch (error) {
        console.error('Rezervasyon oluşturulamadı:', error);
        openNotification('error', 'Hata', 'Rezervasyon oluşturulamadı.');
      } finally {
        setReservationBusy(busyKey, false);
      }
    },
    [setReservationBusy, clearReservationNote],
  );

  const handleDeleteReservation = useCallback(
    async (reservation: PlanningReservation) => {
      if (!reservation || reservation.id === undefined) {
        return;
      }
      const busyKey = `delete-${reservation.id}`;
      setReservationBusy(busyKey, true);
      try {
        await deletePlanningReservation(reservation.id);
        setReservations((prev) => prev.filter((item) => item.id !== reservation.id));
        openNotification('success', 'Başarılı', 'Rezervasyon silindi.');
      } catch (error) {
        console.error('Rezervasyon silinemedi:', error);
        openNotification('error', 'Hata', 'Rezervasyon silinemedi.');
      } finally {
        setReservationBusy(busyKey, false);
      }
    },
    [setReservationBusy],
  );

  const tableData = useMemo(() => {
    if (!weekDays.length) {
      return [];
    }

    const PUBLIC_HOLIDAY_ROW_ID = Number.MIN_SAFE_INTEGER;
    const rows = new Map<number, Record<string, any>>();
    const weekStartBoundary = weekDays[0].startOf('day');
    const weekEndBoundary = weekStartBoundary.add(7, 'day');

    const initRow = (inspectorId: number, info?: any) => {
      const row: Record<string, any> = {
        key: `inspector-${inspectorId}`,
        inspectorId,
        name: getInspectorLabel(info, inspectorId),
        professions: getInspectorProfessionNames(info),
        locationCities: new Set<string>(),
        locationDistricts: new Set<string>(),
        locationSearch: '',
        city: undefined,
        district: undefined,
      };
      weekDays.forEach((day) => {
        row[day.format('YYYY-MM-DD')] = [];
      });
      return row;
    };

    const ensureRow = (inspectorId: number, infoOverride?: any) => {
      if (!rows.has(inspectorId)) {
        const info = infoOverride ?? inspectorInfoMap.get(inspectorId);
        rows.set(inspectorId, initRow(inspectorId, info));
      }
      return rows.get(inspectorId)!;
    };

    const ensureCell = (row: Record<string, any>, key: string): CellEntry[] => {
      if (!Array.isArray(row[key])) {
        row[key] = [];
      }
      return row[key] as CellEntry[];
    };

    effectiveInspectors.forEach((ins: any) => {
      const inspectorId = resolveAccountId(ins);
      if (inspectorId !== undefined) {
        const info = inspectorInfoMap.get(inspectorId) ?? ins;
        rows.set(inspectorId, initRow(inspectorId, info));
      }
    });

    (reservations || []).forEach((reservation) => {
      const accountId = resolveAccountId(reservation);
      if (accountId === undefined) {
        return;
      }
      const inspectorId = accountId;
      const accountInfo = (reservation as any)?.account;
      const info = inspectorInfoMap.get(inspectorId) ?? (accountInfo ? { account: accountInfo } : reservation);
      ensureRow(inspectorId, info);
    });

    weekAppointments.forEach((appointment) => {
      const rawInspector = (appointment as any)?.inspector;
      const inspectorAccountId = resolveAccountId(rawInspector) ?? resolveAccountId(appointment);
      const mappedInspectorId = inspectorAccountId !== undefined ? accountToInspectorId.get(inspectorAccountId) : undefined;
      const inspectorId = mappedInspectorId ?? inspectorAccountId;
      if (inspectorId === undefined) {
        return;
      }
      const planningMoment = getPlanningMoment(appointment);
      if (!planningMoment) {
        return;
      }
      const dayKey = planningMoment.format('YYYY-MM-DD');
      const row = ensureRow(inspectorId);
      const locationCities: Set<string> = row.locationCities instanceof Set ? row.locationCities : new Set<string>();
      const locationDistricts: Set<string> = row.locationDistricts instanceof Set ? row.locationDistricts : new Set<string>();
      const details = getCompanyDetails(appointment);
      if (details.city) {
        locationCities.add(details.city);
      }
      if (details.district) {
        locationDistricts.add(details.district);
      }
      row.locationCities = locationCities;
      row.locationDistricts = locationDistricts;
      const existing = ensureCell(row, dayKey);
      existing.push({ type: 'appointment', appointment, planningMoment });
      existing.sort((a, b) => getEntrySortValue(a) - getEntrySortValue(b));
    });

    (leaves || []).forEach((leave) => {
      const accountId = resolveAccountId(leave);
      if (accountId === undefined) {
        return;
      }
      const mappedInspectorId = accountToInspectorId.get(accountId);
      const explicitInspectorId = resolveAccountId((leave as any)?.inspector);
      const inspectorId = mappedInspectorId ?? explicitInspectorId ?? accountId;
      if (inspectorId === undefined) {
        return;
      }
      const startMoment = dayjs((leave as any)?.startDate);
      if (!startMoment.isValid()) {
        return;
      }
      const rawEndMoment = (leave as any)?.endDate ? dayjs((leave as any)?.endDate) : startMoment;
      const endMoment = rawEndMoment.isValid() ? rawEndMoment : startMoment;
      if (endMoment.isBefore(weekStartBoundary) || !startMoment.isBefore(weekEndBoundary)) {
        return;
      }
      const row = ensureRow(inspectorId);
      weekDays.forEach((day) => {
        const dayKey = day.format('YYYY-MM-DD');
        const dayStart = day.startOf('day');
        const dayEnd = day.endOf('day');
        if (endMoment.isBefore(dayStart) || startMoment.isAfter(dayEnd)) {
          return;
        }
        const cellEntries = ensureCell(row, dayKey);
        const coverStart = startMoment.isAfter(dayStart) ? startMoment : dayStart;
        const coverEnd = endMoment.isBefore(dayEnd) ? endMoment : dayEnd;
        cellEntries.push({
          type: 'leave',
          leave,
          startMoment,
          endMoment,
          dayStart,
          dayEnd,
          coverStart,
          coverEnd,
        });
        cellEntries.sort((a, b) => getEntrySortValue(a) - getEntrySortValue(b));
      });
    });

    const ensurePublicHolidayRow = () => {
      if (!rows.has(PUBLIC_HOLIDAY_ROW_ID)) {
        rows.set(PUBLIC_HOLIDAY_ROW_ID, initRow(PUBLIC_HOLIDAY_ROW_ID, publicHolidayRowInfo));
      }
      return rows.get(PUBLIC_HOLIDAY_ROW_ID)!;
    };

    (publicHolidays || []).forEach((holiday) => {
      const startMoment = holiday?.startDate ? dayjs(holiday.startDate) : null;
      if (!startMoment || !startMoment.isValid()) {
        return;
      }
      const hasEndDate = Boolean(holiday?.endDate);
      const rawEndMoment = hasEndDate ? dayjs(holiday?.endDate) : startMoment;
      const endMoment = rawEndMoment.isValid() ? rawEndMoment : startMoment;
      if (endMoment.isBefore(weekStartBoundary) || !startMoment.isBefore(weekEndBoundary)) {
        return;
      }

      weekDays.forEach((day) => {
        const dayKey = day.format('YYYY-MM-DD');
        const dayStart = day.startOf('day');
        const dayEnd = day.endOf('day');
        if (endMoment.isBefore(dayStart) || startMoment.isAfter(dayEnd)) {
          return;
        }
        const targetRows = rows.size === 0 ? [ensurePublicHolidayRow()] : Array.from(rows.values());
        targetRows.forEach((row) => {
          const cellEntries = ensureCell(row, dayKey);
          const coverStart = startMoment.isAfter(dayStart) ? startMoment : dayStart;
          const coverEnd = endMoment.isBefore(dayEnd) ? endMoment : dayEnd;
          cellEntries.push({
            type: 'publicHoliday',
            holiday,
            startMoment,
            endMoment,
            dayStart,
            dayEnd,
            coverStart,
            coverEnd,
          });
          cellEntries.sort((a, b) => getEntrySortValue(a) - getEntrySortValue(b));
        });
      });
    });

    rows.forEach((row) => {
      const cities = row.locationCities instanceof Set
        ? Array.from(row.locationCities)
        : Array.isArray(row.locationCities)
          ? row.locationCities
          : [];
      const districts = row.locationDistricts instanceof Set
        ? Array.from(row.locationDistricts)
        : Array.isArray(row.locationDistricts)
          ? row.locationDistricts
          : [];
      row.city = cities[0];
      row.district = districts[0];
      row.locationSearch = [...districts, ...cities].filter(Boolean).join(' ');
      row.locationCities = cities;
      row.locationDistricts = districts;
    });

    return Array.from(rows.values());
  }, [
    effectiveInspectors,
    inspectorInfoMap,
    accountToInspectorId,
    weekAppointments,
    weekDays,
    leaves,
    publicHolidays,
    publicHolidayRowInfo,
    reservations,
  ]);

  const today = dayjs().startOf('day');

  const columns: ColumnsType<Record<string, any>> = useMemo(() => {
    const firstColumn = {
      title: 'P.K.M Uzmanı',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left' as const,
      width: 220,
      onHeaderCell: () => ({ style: { backgroundColor: '#eef5ff', fontWeight: 600 } }),
      onCell: () => ({ style: { backgroundColor: '#f7fbff' } }),
      render: (value: string, record: Record<string, any>) => {
        const nameLabel = typeof value === 'string' && value.trim() ? value : record?.name;
        const professionsRaw = Array.isArray(record?.professions) ? record.professions : [];
        const professionList = professionsRaw
          .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
          .filter((entry) => !!entry);
        return (
          <Space direction='vertical' size={6} style={{ width: '100%' }}>
            <Text strong>{nameLabel || '-'}</Text>
            {professionList.length ? (
              <Space size={[4, 4]} wrap>
                {professionList.map((profession: string) => (
                  <Tag key={profession}>
                    {profession}
                  </Tag>
                ))}
              </Space>
            ) : null}
          </Space>
        );
      },
    };

    const dayColumns = weekDays.map((day) => {
      const key = day.format('YYYY-MM-DD');
      const isWeekend = day.day() === 0 || day.day() === 6;
      return {
        title: day.format('DD MMM ddd'),
        dataIndex: key,
        key,
        align: 'left' as const,
        width: 220,
        onHeaderCell: () => ({ style: { backgroundColor: isWeekend ? '#fff7e6' : undefined } }),
        onCell: () => ({ style: { backgroundColor: isWeekend ? '#fff7e6' : undefined } }),
        render: (entries?: CellEntry[], record?: Record<string, any>) => {
          const inspectorIdValue = toNumber(record?.inspectorId);
          const inspectorId = typeof inspectorIdValue === 'number' && !Number.isNaN(inspectorIdValue)
            ? inspectorIdValue
            : undefined;
          const isPublicHolidayRow = inspectorIdValue === Number.MIN_SAFE_INTEGER;
          const dayEntries = Array.isArray(entries) ? entries : [];
          const reservationKey = inspectorId !== undefined ? `${inspectorId}-${key}` : undefined;
          const reservation = reservationKey ? reservationLookup.get(reservationKey) : undefined;
          const isPastDay = day.startOf('day').isBefore(today);
          const hasReservation = Boolean(reservation);
          const reservationMoment = reservation ? dayjs((reservation as any)?.createdAt) : null;
          const reservationDateLabel = reservationMoment && reservationMoment.isValid()
            ? reservationMoment.format('DD MMM YYYY HH:mm')
            : undefined;
          const reservationNoteValue = reservationKey ? reservationNotes[reservationKey] ?? '' : '';
          const reservationOwnerRaw = reservation?.createdBy || reservation?.updatedBy;
          const reservationOwnerNormalized = reservationOwnerRaw
            ? reservationOwnerRaw.toString().trim().toLowerCase()
            : undefined;
          const reservationNoteText = reservation?.note?.trim();
          const canDeleteReservation = isAdmin || (
            !!currentUserEmail && !!reservationOwnerNormalized && reservationOwnerNormalized === currentUserEmail
          );
          const reservationTooltipContent = reservation ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div>
                {reservationNoteText || 'Belirtilmedi'}
              </div>
              <div>
                {reservationOwnerRaw || 'Belirtilmedi'}
              </div>
              {reservationDateLabel ? (
                <div>
                  {reservationDateLabel}
                </div>
              ) : null}
            </div>
          ) : null;
          const hasEntries = dayEntries.length > 0;
          const canReserve = !isPublicHolidayRow && inspectorId !== undefined && !isPastDay;
          if (!hasEntries && !hasReservation && !canReserve) {
            return <Text type='secondary'>-</Text>;
          }
          const createBusyKey = canReserve ? `create-${inspectorId}-${key}` : undefined;
          const isCreateBusy = createBusyKey ? reservationBusyKeys.has(createBusyKey) : false;
          const deleteBusyKey = reservation?.id !== undefined ? `delete-${reservation.id}` : undefined;
          const isDeleteBusy = deleteBusyKey ? reservationBusyKeys.has(deleteBusyKey) : false;
          return (
            <Space direction='vertical' size={6} style={{ width: '100%' }}>
              {dayEntries.map((entry, idx) => {
                if (entry.type === 'publicHoliday') {
                  const { holiday, coverStart, coverEnd, dayStart, dayEnd, startMoment, endMoment } = entry;
                  const holidayLabel = holiday?.name?.toString() || 'Resmi Tatil';
                  const isHalfDay = Boolean(holiday?.halfDay);
                  const timeLabel = isHalfDay ? 'Yarım Gün' : 'Tam Gün';
                  const startLabel = startMoment && startMoment.isValid()
                    ? startMoment.format('DD MMM YYYY')
                    : '-';
                  const endLabel = endMoment && endMoment.isValid()
                    ? endMoment.format('DD MMM YYYY')
                    : '-';
                  const tooltipContent = (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div>
                        <strong>Resmi Tatil:</strong> {holidayLabel}
                      </div>
                      <div>
                        <strong>Süre:</strong> {timeLabel}
                      </div>
                      <div>
                        <strong>Başlangıç:</strong> {startLabel}
                      </div>
                      <div>
                        <strong>Bitiş:</strong> {endLabel}
                      </div>
                    </div>
                  );
                  const keyValue = `public-holiday-${holiday?.id ?? startMoment.valueOf()}-${idx}-${dayStart.valueOf()}`;
                  return (
                    <Tooltip key={keyValue} title={tooltipContent} placement='topLeft'>
                      <Tag
                        color={statusColorMap.PUBLIC_HOLIDAY ?? 'magenta'}
                        style={{
                          fontSize: 12,
                          padding: '2px 8px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          borderRadius: 6,
                        }}
                      >
                        <span>{holidayLabel}</span>
                        <span>{timeLabel}</span>
                      </Tag>
                    </Tooltip>
                  );
                }

                if (entry.type === 'leave') {
                  const { leave, coverStart, coverEnd, dayStart, dayEnd, startMoment, endMoment } = entry;
                  const typeKey = typeof leave?.type === 'string' ? leave.type : '';
                  const leaveLabel = leaveTypeMap[typeKey] ?? 'İzin';
                  const isAllDay = coverStart.isSame(dayStart) && coverEnd.isSame(dayEnd);
                  const timeLabel = isAllDay ? 'Tam Gün' : `${coverStart.format('HH:mm')} - ${coverEnd.format('HH:mm')}`;
                  const startLabel = startMoment && startMoment.isValid()
                    ? startMoment.format('DD MMM YYYY HH:mm')
                    : '-';
                  const endLabel = endMoment && endMoment.isValid()
                    ? endMoment.format('DD MMM YYYY HH:mm')
                    : '-';
                  const noteValue = (leave as any)?.note;
                  const tooltipContent = (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div>
                        <strong>İzin Türü:</strong> {leaveLabel}
                      </div>
                      <div>
                        <strong>Başlangıç:</strong> {startLabel}
                      </div>
                      <div>
                        <strong>Bitiş:</strong> {endLabel}
                      </div>
                      {noteValue ? (
                        <div>
                          <strong>Not:</strong> {String(noteValue)}
                        </div>
                      ) : null}
                    </div>
                  );
                  const keyValue = `leave-${leave?.id ?? startMoment.valueOf()}-${idx}-${dayStart.valueOf()}`;
                  return (
                    <Tooltip key={keyValue} title={tooltipContent} placement='topLeft'>
                      <Tag
                        color={statusColorMap.LEAVE ?? 'volcano'}
                        style={{
                          fontSize: 12,
                          padding: '2px 8px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          borderRadius: 6,
                        }}
                      >
                        <span>{leaveLabel}</span>
                        <span>{timeLabel}</span>
                      </Tag>
                    </Tooltip>
                  );
                }

                const { appointment, planningMoment } = entry;
                const status = ((appointment as any)?.status || 'PLANNED').toString().toUpperCase();
                const color = statusColorMap[status] ?? 'blue';
                const rawProposalNumber =
                  (appointment as any)?.proposal?.proposalNumber ?? (appointment as any)?.proposalNumber ?? undefined;
                const proposalNumber = rawProposalNumber !== undefined && rawProposalNumber !== null
                  ? String(rawProposalNumber)
                  : undefined;
                const startTimeLabel = planningMoment && planningMoment.isValid() ? planningMoment.format('HH:mm') : '--:--';
                const endMomentRaw =
                  (appointment as any)?.endDate ??
                  (appointment as any)?.planningEndDate ??
                  (appointment as any)?.end_date;
                const endMoment = endMomentRaw ? dayjs(endMomentRaw) : null;
                const endTimeLabel = endMoment && endMoment.isValid() ? endMoment.format('HH:mm') : '--:--';
                const keyValue = `${(appointment as any)?.id ?? `${planningMoment.valueOf()}`}-${idx}`;
                const details = getCompanyDetails(appointment);
                const locationLabel = [details.district, details.city].filter(Boolean).join(' / ') || '-';
                const companyLabel = details.name ?? '-';
                const proposalLabel = proposalNumber ?? '-';
                const timeRangeLabel = `${startTimeLabel} - ${endTimeLabel}`;
                const tooltipContent = (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div>
                      <strong>Firma Adı:</strong> {details.name ?? '-'}
                    </div>
                    <div>
                      <strong>Adres:</strong> {details.address ?? '-'}
                    </div>
                    <div>
                      <strong>İl:</strong> {details.city ?? '-'}
                    </div>
                    <div>
                      <strong>İlçe:</strong> {details.district ?? '-'}
                    </div>
                  </div>
                );
                return (
                  <Tooltip key={keyValue} title={tooltipContent} placement='top'>
                    <Tag
                      color={color}
                      style={{
                        fontSize: 12,
                        padding: '2px 8px',
                        display: 'block',
                        width: '100%',
                        borderRadius: 6,
                        whiteSpace: 'normal',
                      }}
                    >
                      <div>{proposalLabel}</div>
                      <div>{companyLabel}</div>
                      <div>{locationLabel}</div>
                      <div>{timeRangeLabel}</div>
                    </Tag>
                  </Tooltip>
                );
              })}
              {reservation ? (
                <Space
                  key={`reservation-${reservation.id ?? reservationKey}`}
                  align='center'
                  size={8}
                  wrap
                >
                  <Tooltip title={reservationTooltipContent || 'Rezerve'}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        backgroundColor: 'lightgray',
                        color: 'black',
                      }}
                    >
                      <CalendarCheckIn01Icon size={18} color='black' />
                    </span>
                  </Tooltip>
                  {canDeleteReservation ? (
                    <Popconfirm
                      title='Rezervasyonu silmek istediğinize emin misiniz?'
                      okText='Evet'
                      cancelText='Vazgeç'
                      onConfirm={() => handleDeleteReservation(reservation)}
                      okButtonProps={{ loading: isDeleteBusy }}
                      disabled={isDeleteBusy}
                    >
                      <Button
                        size='small'
                        danger
                        loading={isDeleteBusy}
                        disabled={isDeleteBusy}
                      >
                        Sil
                      </Button>
                    </Popconfirm>
                  ) : null}
                </Space>
              ) : null}
              {canReserve && !hasEntries && !hasReservation ? (
                <Popconfirm
                  title='Bu günü rezerve etmek istediğinize emin misiniz?'
                  okText='Evet'
                  cancelText='Vazgeç'
                  onConfirm={() => handleCreateReservation(inspectorId, key, reservationNoteValue)}
                  onCancel={() => clearReservationNote(reservationKey)}
                  okButtonProps={{ disabled: isCreateBusy || !(reservationNoteValue?.trim()) }}
                  description={
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <Text strong>Not</Text>
                      <Input.TextArea
                        className='border-gray'
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        value={reservationNoteValue}
                        onChange={(event) => updateReservationNote(reservationKey, event.target.value)}
                        placeholder='Rezerve sebebini yazın (Firma Adı, Verilen Görev gibi)'
                        disabled={isCreateBusy}
                        required
                      />
                    </div>
                  }
                  disabled={isCreateBusy}
                >
                  <Tooltip title='Rezerve Et'>
                    <button
                      type='button'
                      aria-label='Rezerve Et'
                      aria-disabled={isCreateBusy}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                        minWidth: 32,
                        minHeight: 32,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isCreateBusy ? 'not-allowed' : 'pointer',
                        color: '#1677ff',
                        opacity: isCreateBusy ? 0.6 : 1,
                      }}
                    >
                      {isCreateBusy ? (
                        <LoadingOutlined spin style={{ fontSize: 20 }} />
                      ) : (
                        <PlusSignSquareIcon size={22} color='#1677ff' />
                      )}
                    </button>
                  </Tooltip>
                </Popconfirm>
              ) : null}
            </Space>
          );
        },
      };
    });

    const shouldShowInspectorColumn = !isInspector;
    return shouldShowInspectorColumn ? [firstColumn, ...dayColumns] : dayColumns;
  }, [
    weekDays,
    isInspector,
    reservationLookup,
    reservationBusyKeys,
    handleCreateReservation,
    handleDeleteReservation,
    reservationNotes,
    updateReservationNote,
    clearReservationNote,
    today,
    isAdmin,
    currentUserEmail,
  ]);

  const isLoading = appointmentLoading || leaveLoading || publicHolidayLoading || reservationLoading;
  const normalizedSearch = useMemo(() => searchTerm.trim().toLocaleUpperCase('tr-TR'), [searchTerm]);
  const filteredTableData = useMemo(() => {
    if (!normalizedSearch) {
      return tableData;
    }
    const includesSearch = (value: any): boolean => {
      if (value === null || value === undefined) {
        return false;
      }
      const normalized = value.toString().trim().toLocaleUpperCase('tr-TR');
      if (!normalized) {
        return false;
      }
      return normalized.includes(normalizedSearch);
    };
    return tableData.filter((row) => {
      const nameMatch = includesSearch(row?.name);
      const professions = Array.isArray(row?.professions) ? row.professions : [];
      const professionMatch = professions.some((item) => includesSearch(item));
      const locationValues = [
        row?.locationSearch,
        row?.city,
        row?.district,
      ];
      const locationMatch = locationValues.some((val) => includesSearch(val));
      return nameMatch || professionMatch || locationMatch;
    });
  }, [tableData, normalizedSearch]);

  const handlePrevWeek = () => onWeekStartChange(weekStart.add(-7, 'day'));
  const handleNextWeek = () => {
    const nextWeekStart = weekStart.add(7, 'day');
    const nextWeekStartValue = nextWeekStart.startOf('day').valueOf();
    if (maxForwardTimestamp !== null && nextWeekStartValue > maxForwardTimestamp) {
      if (inspectorFutureLimit !== undefined) {
        openNotification(
          'warning',
          'Uyarı',
          `En fazla ${inspectorFutureLimit} gün sonrasına kadar görüntüleyebilirsiniz.`,
        );
      }
      return;
    }
    onWeekStartChange(nextWeekStart);
  };
  const handleCurrentWeek = () => onWeekStartChange(dayjs().startOf('isoWeek'));
  const handleMonthChange = (value: Dayjs | null) => {
    if (!value || !value.isValid()) {
      return;
    }

    const selectedWeekStart = value.startOf('month').startOf('isoWeek');
    const selectedWeekStartValue = selectedWeekStart.startOf('day').valueOf();
    if (maxForwardTimestamp !== null && selectedWeekStartValue > maxForwardTimestamp) {
      if (inspectorFutureLimit !== undefined) {
        openNotification(
          'warning',
          'Uyarı',
          `En fazla ${inspectorFutureLimit} gün sonrasına kadar görüntüleyebilirsiniz.`,
        );
      }
      return;
    }

    onWeekStartChange(selectedWeekStart);
  };

  const focusedMonth = weekStart.add(3, 'day').startOf('month');
  const rangeLabel = weekDays.length
    ? `${weekDays[0].format('DD MMM YYYY')} - ${weekDays[6].format('DD MMM YYYY')}`
    : '';

  return (
    <Drawer
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            width: '100%',
          }}
        >
          <Space align='center' size={12} wrap>
            <Button onClick={handlePrevWeek}>Önceki Hafta</Button>
            <DatePicker
              picker='month'
              allowClear={false}
              value={focusedMonth}
              onChange={handleMonthChange}
              format='MMMM YYYY'
              placeholder='Ay / Yıl seçiniz'
              style={{ width: 180 }}
            />
            <Button onClick={handleNextWeek} disabled={!canNavigateForward}>Sonraki Hafta</Button>
            <Button onClick={handleCurrentWeek} className='action-button'>
              Bu Hafta
            </Button>
            <div
              style={{
                padding: '8px 14px',
                border: '1px solid #f0f0f0',
                borderRadius: 10,
                background: '#fafafa',
                minWidth: 220,
                textAlign: 'center',
              }}
            >
              <Text strong>{rangeLabel}</Text>
            </div>
          </Space>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
                {canViewPublicHolidays && (
              <Button onClick={() => window.open('/settings/public-holidays', '_blank')}>
                Resmi Tatiller
              </Button>
            )}
            <Input
              allowClear
              style={{ width: 300, maxWidth: '100%' }}
              placeholder='İl-İlçe-Meslek-Uzman Ara'
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
        
          </div>
        </div>
      }
      placement='right'
      width={"90%"}
      onClose={onClose}
      open={open}
      destroyOnClose
    >
      <Space direction='vertical' size={12} style={{ width: '100%' }}>
        <Table
          dataSource={filteredTableData}
          columns={columns}
          loading={isLoading}
          pagination={false}
          scroll={{ x: true }}
          size='small'
          rowKey='key'
        />
      </Space>
    </Drawer>
  );
};

export default WeeklyPlanDrawer;
